import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/* ------------------------------------------------------------------- types */
export interface Allowance {
  name: string;
  amount: number;
  taxable: boolean;
}
export interface Employee {
  id: string;
  employeeNo: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  position: string;
  department: string;
  hireDate: string;
  endDate: string | null;
  status: 'ACTIVE' | 'ENDED';
  staffUserId: string | null;
  // only with hr.manage
  dateOfBirth?: string | null;
  emergencyName?: string | null;
  emergencyPhone?: string | null;
  notes?: string | null;
  // only with hr.pay_data
  basicSalary?: number;
  allowances?: Allowance[];
  payMethod?: 'BANK' | 'MOMO' | 'CASH';
  payBankName?: string | null;
  payAccount?: string | null;
  tin?: string | null;
  pensionNo?: string | null;
}
export interface LeaveType {
  id: string;
  name: string;
  daysPerYear: number;
  paid: boolean;
}
export interface LeaveRequest {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeNo: string;
  leaveType: string;
  paid: boolean;
  from: string;
  to: string;
  days: number;
  reason: string | null;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  decisionNote: string | null;
}
export interface LeaveBalance {
  leaveType: string;
  paid: boolean;
  entitlement: number;
  taken: number;
  pending: number;
  remaining: number | null;
}
export interface Loan {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeNo: string;
  principal: number;
  monthlyRepayment: number;
  balance: number;
  status: 'ACTIVE' | 'CLEARED';
  issuedOn: string;
  note: string | null;
}
export interface TaxBand {
  upTo: number | null;
  rateBps: number;
}
export interface RulesConfig {
  pension: { employeeBps: number; employerBps: number; base: 'BASIC' | 'GROSS' };
  pensionDeductibleForTax: boolean;
  taxBands: TaxBand[];
  unpaidDayDivisor: number;
}
export interface RuleSet {
  id: string;
  name: string;
  effectiveFrom: string;
  config: RulesConfig;
  approved: boolean;
  approvedAt: string | null;
}
export interface RunTotals {
  headcount: number;
  gross: number;
  pensionEmployee: number;
  pensionEmployer: number;
  paye: number;
  loanRepayment: number;
  otherDeductions: number;
  net: number;
  employerCost: number;
}
export interface Run {
  id: string;
  period: string;
  status: 'CALCULATED' | 'APPROVED' | 'PAID';
  totals: RunTotals;
  ruleSet?: string;
  calculatedAt: string;
  approvedAt: string | null;
  paidAt: string | null;
  paidMethod: string | null;
}
export interface RunLine {
  employeeId: string;
  employeeNo: string;
  employeeName: string;
  position: string;
  basic: number;
  unpaidDays: number;
  unpaidDeduction: number;
  basicPaid: number;
  allowances: Allowance[];
  allowancesTotal: number;
  extraEarnings: number;
  extraNote: string | null;
  gross: number;
  pensionEmployee: number;
  pensionEmployer: number;
  taxableIncome: number;
  paye: number;
  loanRepayment: number;
  otherDeductions: number;
  otherNote: string | null;
  net: number;
  employerCost: number;
  warnings: string[];
}
export interface Payslip extends RunLine {
  period: string;
  status: string;
  employer: { name: string; address: string | null; phone: string | null };
  employee: { id: string; employeeNo: string; name: string; position: string; department: string; hireDate: string | null; payMethod: string | null };
  yearToDate: { gross: number; paye: number; pension: number; net: number };
}
export interface MyPayslip {
  runId: string;
  period: string;
  status: string;
  gross: number;
  net: number;
}

/* ----------------------------------------------------------------- queries */
export const useEmployees = (status?: 'ACTIVE' | 'ENDED') => useQuery({ queryKey: ['hr', 'employees', status ?? 'all'], queryFn: () => api<{ employees: Employee[] }>(`/hr/employees${status ? `?status=${status}` : ''}`).then((r) => r.employees) });
export const useEmployee = (id: string | null) => useQuery({ queryKey: ['hr', 'employee', id], enabled: !!id, queryFn: () => api<Employee>(`/hr/employees/${id}`) });
export const useLeaveTypes = () => useQuery({ queryKey: ['hr', 'leave-types'], queryFn: () => api<{ leaveTypes: LeaveType[] }>('/hr/leave-types').then((r) => r.leaveTypes) });
export const useLeaveRequests = (status: string) => useQuery({ queryKey: ['hr', 'leave', status], queryFn: () => api<{ requests: LeaveRequest[] }>(`/hr/leave-requests${status ? `?status=${status}` : ''}`).then((r) => r.requests) });
export const useLeaveBalance = (id: string | null) => useQuery({ queryKey: ['hr', 'balance', id], enabled: !!id, queryFn: () => api<{ year: number; balances: LeaveBalance[] }>(`/hr/employees/${id}/leave-balance`) });
export const useLoans = (enabled: boolean) => useQuery({ queryKey: ['hr', 'loans'], enabled, queryFn: () => api<{ loans: Loan[] }>('/hr/loans').then((r) => r.loans) });
export const useRuleSets = () => useQuery({ queryKey: ['payroll', 'rules'], queryFn: () => api<{ ruleSets: RuleSet[] }>('/payroll/rules').then((r) => r.ruleSets) });
export const useRuns = () => useQuery({ queryKey: ['payroll', 'runs'], queryFn: () => api<{ runs: Run[] }>('/payroll/runs').then((r) => r.runs) });
export const useRun = (id: string) => useQuery({ queryKey: ['payroll', 'run', id], queryFn: () => api<Run & { lines: RunLine[]; calculatedById: string }>(`/payroll/runs/${id}`) });
export const usePayslip = (path: string) => useQuery({ queryKey: ['payroll', 'payslip', path], queryFn: () => api<Payslip>(path) });
export const useMyPayslips = () => useQuery({ queryKey: ['payroll', 'mine'], queryFn: () => api<{ payslips: MyPayslip[] }>('/me/payslips').then((r) => r.payslips), retry: false });
export const useMyLeave = () => useQuery({ queryKey: ['hr', 'mine'], queryFn: () => api<{ requests: LeaveRequest[]; year: number; balances: LeaveBalance[] }>('/me/leave'), retry: false });

/** HR and payroll changes can touch one another (a raise stales a payroll), so they refresh together. */
export function useHrMutation<V, R = unknown>(run: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      for (const k of ['hr', 'payroll', 'daily']) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
}
